import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, FileText, ArrowRight, Camera, FolderLock } from "lucide-react";
import UnreadBadge from "@/components/UnreadBadge";
import { formatStaleness, isStale } from "@/lib/dates";

const STATUS_STYLES = {
  submitted: "bg-amber-100 text-amber-900 border-amber-200",
  in_review: "bg-blue-100 text-blue-900 border-blue-200",
  approved: "bg-emerald-100 text-emerald-900 border-emerald-200",
  closed: "bg-gray-100 text-gray-700 border-gray-200",
};

export default function PolicyholderDashboard() {
  const { user } = useAuth();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/claims")
      .then((r) => setClaims(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="policyholder-dashboard">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#C25934] font-medium mb-2">
            Welcome back, {user?.full_name?.split(" ")[0]}
          </p>
          <h1 className="font-['Outfit'] text-4xl font-semibold tracking-tight">
            Your claims
          </h1>
        </div>
        <Link to="/onboarding" data-testid="new-claim-button">
          <Button className="btn-primary rounded-full px-6">
            <Plus className="w-4 h-4 mr-2" /> File a new claim
          </Button>
        </Link>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-10">
        <Card className="border-[#E5E7EB]">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2 text-[#C25934]">
              <Camera className="w-5 h-5" />
              <p className="text-xs uppercase tracking-widest font-medium">
                AI photo capture
              </p>
            </div>
            <p className="text-gray-700 text-sm">
              Capture damage with overlay guidance from any claim's detail page.
            </p>
          </CardContent>
        </Card>
        <Card className="border-[#E5E7EB]">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2 text-[#C25934]">
              <FolderLock className="w-5 h-5" />
              <p className="text-xs uppercase tracking-widest font-medium">
                Document vault
              </p>
            </div>
            <p className="text-gray-700 text-sm">
              Drag-drop estimates, declarations, and prior claim history.
            </p>
          </CardContent>
        </Card>
        <Card className="border-[#E5E7EB] bg-[#1E3F33] text-[#F9F8F6]">
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-widest text-[#C25934] font-medium mb-2">
              Carrier guides
            </p>
            <p className="text-sm text-[#F9F8F6]/80">
              Open any claim to see your carrier-specific Proof of Loss
              instructions.
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-[#E5E7EB]">
        <CardHeader>
          <CardTitle className="font-['Outfit'] text-xl">
            All claims
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-gray-500 py-8 text-center">Loading…</p>
          ) : claims.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-10 h-10 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 mb-4">
                You haven't filed a claim yet.
              </p>
              <Link to="/onboarding" data-testid="empty-new-claim-button">
                <Button className="btn-primary rounded-full">
                  Start your first claim
                </Button>
              </Link>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date of loss</TableHead>
                  <TableHead>Damage</TableHead>
                  <TableHead>Carrier</TableHead>
                  <TableHead>Last activity</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {claims.map((c) => (
                  <TableRow key={c.id} data-testid={`claim-row-${c.id}`}>
                    <TableCell>{c.date_of_loss}</TableCell>
                    <TableCell className="capitalize">
                      <div className="flex items-center gap-2">
                        {c.damage_type}
                        <UnreadBadge count={c.unread_count} claimId={c.id} />
                      </div>
                    </TableCell>
                    <TableCell>{c.carrier}</TableCell>
                    <TableCell
                      className={
                        isStale(c.last_activity_at)
                          ? "text-[#C25934] font-medium"
                          : "text-gray-700"
                      }
                      data-testid={`ph-last-activity-${c.id}`}
                    >
                      {formatStaleness(c.last_activity_at)}
                    </TableCell>
                    <TableCell>
                      <span
                        className={`text-xs px-2.5 py-1 rounded-full border ${
                          STATUS_STYLES[c.status] || STATUS_STYLES.submitted
                        }`}
                      >
                        {c.status?.replace("_", " ")}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Link to={`/claim/${c.id}`} data-testid={`open-claim-${c.id}`}>
                        <Button variant="ghost" size="sm">
                          Open <ArrowRight className="w-3.5 h-3.5 ml-1" />
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
